import { useState } from 'react';
import { visibleOptionLabel } from '@/lib/catalog/optionLabel';
import {
  Badge, Button, RadioGroup, SectionCard, Select, Skeleton,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  toast,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import type { AdminMeResponse, OrderDetail } from '@/lib/api-admin/types';
import {
  ADMIN_MEMO_CATEGORIES,
  ADMIN_MEMO_MAX_LENGTH,
  useOrderAdminMemoMutations,
  useOrderAdminMemosQuery,
  type AdminMemoCategory,
  type AdminMemoScope,
  type OrderAdminMemo,
} from '../hooks/useOrderAdminMemos';
import { formatKst } from './OrderAdminMemoSection';

/** 메모를 등록할 수 있는 역할 — be-order BackofficeOrderAdminMemoController.create @PreAuthorize 와 같다. */
const WRITE_ROLES = ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT', 'FULFILLMENT_AGENT'] as const;

const CATEGORY_LABEL: Record<AdminMemoCategory, string> = {
  RECEIVED: '접수',
  DONE: '완료',
  DEPOSIT: '입금',
  SHIPPING: '배송',
  SOLD_OUT: '품절',
  EXCHANGE: '교환',
  RETURN: '반품',
  REFUND: '환불',
  AS: 'A/S',
  ETC: '기타',
};

const textareaClass =
  'w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2';
const textareaStyle = {
  background: 'var(--surface-0)',
  color: 'var(--content-strong)',
  borderColor: 'var(--divider-strong)',
} as const;

const CategorySelect = ({
  value,
  onChange,
  testId,
}: {
  value: AdminMemoCategory | '';
  onChange: (v: AdminMemoCategory | '') => void;
  testId: string;
}) => {
  const { t } = useTranslation('orders');
  return (
    <Select
      aria-label={t('orders.adminMemos.category', '구분')}
      value={value}
      onChange={(e) => onChange(e.target.value as AdminMemoCategory | '')}
      className="w-32"
      data-testid={testId}
    >
      <option value="">{t('orders.adminMemos.categoryNone', '구분 없음')}</option>
      {ADMIN_MEMO_CATEGORIES.map((c) => (
        <option key={c} value={c}>
          {t(`orders.adminMemos.categories.${c}`, CATEGORY_LABEL[c])}
        </option>
      ))}
    </Select>
  );
};

/** 403/404 는 상황에 맞는 문구로, 나머지는 공통 안내. */
const memoErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof ApiClientError) {
    if (e.status === 403) return '작성자 본인이나 최고관리자만 수정·삭제할 수 있습니다.';
    if (e.status === 404) return '이미 삭제됐거나 없는 메모입니다. 목록을 새로 불러왔습니다.';
    if (e.status === 400) return `메모 내용을 확인해 주세요(1~${ADMIN_MEMO_MAX_LENGTH.toLocaleString()}자).`;
  }
  return friendlyMessage(e, fallback);
};

/**
 * QA-292 — 주문 상세 "관리자 메모"(여러 건). 고도몰처럼 주문번호별·상품주문번호별로 남기고 구분을 단다.
 * 수정·삭제 버튼은 작성자 본인(writtenById === 로그인 관리자 ID) 또는 SUPER_ADMIN 에게만 보인다
 * (BE 도 같은 조건으로 403). 이관 메모(godo:…)는 결과적으로 SUPER_ADMIN 만 고칠 수 있다.
 */
export const OrderAdminMemoBoard = ({
  order,
  currentAdmin: me,
}: {
  order: OrderDetail;
  /** 로그인 관리자(주문 상세 페이지가 AuthProvider 에서 넘긴다). */
  currentAdmin: Pick<AdminMeResponse, 'adminUserId' | 'roles'> | null | undefined;
}) => {
  const { t } = useTranslation('orders');
  const roles: ReadonlyArray<string> = me?.roles ?? [];
  const isSuper = roles.includes('SUPER_ADMIN');
  const canWrite = WRITE_ROLES.some((r) => roles.includes(r));
  const canModify = (m: OrderAdminMemo) => isSuper || (!!me?.adminUserId && m.writtenById === me.adminUserId);

  const query = useOrderAdminMemosQuery(order.orderId);
  const { create, update, remove } = useOrderAdminMemoMutations(order.orderId);
  const items = order.items ?? [];
  const itemLabel = (orderItemId: string | null) => {
    const it = items.find((i) => i.orderItemId === orderItemId);
    if (!it) return orderItemId ?? '';
    const option = visibleOptionLabel(it.variantLabel);
    return option ? `${it.productName} (${option})` : it.productName;
  };

  // 등록 폼
  const [scope, setScope] = useState<AdminMemoScope>('ORDER');
  const [orderItemId, setOrderItemId] = useState('');
  const [category, setCategory] = useState<AdminMemoCategory | ''>('');
  const [content, setContent] = useState('');
  const trimmed = content.trim();
  const formValid =
    trimmed.length > 0 && trimmed.length <= ADMIN_MEMO_MAX_LENGTH && (scope === 'ORDER' || orderItemId !== '');

  const submit = async () => {
    if (!formValid) return;
    try {
      await create.mutateAsync({
        scope,
        ...(scope === 'ITEM' ? { orderItemId } : {}),
        ...(category ? { category } : {}),
        content: trimmed,
      });
      setContent('');
      setCategory('');
      toast.success(t('orders.adminMemos.created', '메모를 등록했습니다.'));
    } catch (e) {
      toast.error(memoErrorMessage(e, t('orders.adminMemos.createFailed', '메모를 등록하지 못했습니다.')));
    }
  };

  // 수정
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState<AdminMemoCategory | ''>('');
  const [editContent, setEditContent] = useState('');
  const startEdit = (m: OrderAdminMemo) => {
    setEditingId(m.memoId);
    setEditCategory(m.category ?? '');
    setEditContent(m.content);
  };
  const saveEdit = async () => {
    if (!editingId) return;
    const c = editContent.trim();
    if (!c || c.length > ADMIN_MEMO_MAX_LENGTH) return;
    try {
      // 구분은 항상 현재 값을 같이 보낸다(빼면 BE 가 "구분 없음"으로 바꾼다).
      await update.mutateAsync({ memoId: editingId, body: { category: editCategory || null, content: c } });
      setEditingId(null);
      toast.success(t('orders.adminMemos.updated', '메모를 수정했습니다.'));
    } catch (e) {
      toast.error(memoErrorMessage(e, t('orders.adminMemos.updateFailed', '메모를 수정하지 못했습니다.')));
    }
  };

  // 삭제
  const [deleteTarget, setDeleteTarget] = useState<OrderAdminMemo | null>(null);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove.mutateAsync(deleteTarget.memoId);
      toast.success(t('orders.adminMemos.deleted', '메모를 삭제했습니다.'));
    } catch (e) {
      toast.error(memoErrorMessage(e, t('orders.adminMemos.deleteFailed', '메모를 삭제하지 못했습니다.')));
    } finally {
      setDeleteTarget(null);
    }
  };

  const memos = query.data ?? [];

  return (
    <SectionCard
      title={`${t('orders.adminMemos.title', '관리자 메모')} (${memos.length})`}
      data-testid="order-admin-memo-board"
    >
      {/* 메모에 고객 이름·연락처가 들어 있을 수 있어 Sentry 리플레이에서 가린다. */}
      <div className="space-y-4" data-sentry-mask>
        {query.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : query.isError ? (
          <div className="flex items-center gap-2 text-sm" role="alert" style={{ color: 'var(--signal-danger)' }}>
            {t('orders.adminMemos.loadFailed', '관리자 메모를 불러오지 못했습니다.')}
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              {t('orders.adminMemos.retry', '다시 시도')}
            </Button>
          </div>
        ) : memos.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
            {t('orders.adminMemos.empty', '등록된 관리자 메모가 없습니다.')}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-memo-list">
            {memos.map((m) => {
              const editing = editingId === m.memoId;
              const updated = formatKst(m.updatedAt);
              return (
                <li
                  key={m.memoId}
                  className="rounded-md px-3 py-2"
                  style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-soft)' }}
                  data-testid="admin-memo-item"
                >
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[color:var(--content-muted)]">
                    <Badge size="sm" variant={m.scope === 'ITEM' ? 'info' : 'neutral'}>
                      {m.scope === 'ITEM'
                        ? t('orders.adminMemos.scopeItemShort', '상품')
                        : t('orders.adminMemos.scopeOrderShort', '주문')}
                    </Badge>
                    {m.scope === 'ITEM' ? (
                      <span className="max-w-[320px] truncate" title={itemLabel(m.orderItemId)}>
                        {itemLabel(m.orderItemId)}
                      </span>
                    ) : null}
                    {m.category && !editing ? (
                      <Badge size="sm" variant="warn" data-testid="admin-memo-category">
                        {t(`orders.adminMemos.categories.${m.category}`, CATEGORY_LABEL[m.category] ?? m.category)}
                      </Badge>
                    ) : null}
                    <span className="tabular-nums">{formatKst(m.writtenAt)}</span>
                    {m.writtenBy ? <span>{m.writtenBy}</span> : null}
                    {updated ? (
                      <span className="tabular-nums" data-testid="admin-memo-updated">
                        ({t('orders.adminMemos.updatedAt', '수정')} {updated})
                      </span>
                    ) : null}
                    {canModify(m) && !editing ? (
                      <span className="ml-auto flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(m)} data-testid="admin-memo-edit">
                          {t('orders.adminMemos.edit', '수정')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(m)}
                          data-testid="admin-memo-delete"
                          style={{ color: 'var(--signal-danger)' }}
                        >
                          {t('orders.adminMemos.delete', '삭제')}
                        </Button>
                      </span>
                    ) : null}
                  </div>
                  {editing ? (
                    <div className="space-y-2">
                      <CategorySelect value={editCategory} onChange={setEditCategory} testId="admin-memo-edit-category" />
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        maxLength={ADMIN_MEMO_MAX_LENGTH}
                        rows={3}
                        className={textareaClass}
                        style={textareaStyle}
                        aria-label={t('orders.adminMemos.content', '내용')}
                        data-testid="admin-memo-edit-content"
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                          {t('orders.adminMemos.cancel', '취소')}
                        </Button>
                        <Button
                          size="sm"
                          disabled={update.isPending || !editContent.trim()}
                          onClick={() => void saveEdit()}
                          data-testid="admin-memo-edit-save"
                        >
                          {t('orders.adminMemos.save', '저장')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap break-words text-sm text-[color:var(--content-strong)]">{m.content}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {canWrite ? (
          <div className="space-y-2 border-t pt-3" style={{ borderColor: 'var(--divider-soft)' }} data-testid="admin-memo-form">
            <div className="flex flex-wrap items-center gap-3">
              <RadioGroup
                value={scope}
                onValueChange={(v) => setScope(v as AdminMemoScope)}
                options={[
                  { value: 'ORDER', label: t('orders.adminMemos.scopeOrder', '주문번호별') },
                  { value: 'ITEM', label: t('orders.adminMemos.scopeItem', '상품주문번호별') },
                ]}
              />
              {scope === 'ITEM' ? (
                <Select
                  aria-label={t('orders.adminMemos.item', '대상 상품')}
                  value={orderItemId}
                  onChange={(e) => setOrderItemId(e.target.value)}
                  className="w-72"
                  data-testid="admin-memo-item-select"
                >
                  <option value="">{t('orders.adminMemos.itemPlaceholder', '상품 선택')}</option>
                  {items.map((i) => (
                    <option key={i.orderItemId} value={i.orderItemId}>
                      {itemLabel(i.orderItemId)}
                    </option>
                  ))}
                </Select>
              ) : null}
              <CategorySelect value={category} onChange={setCategory} testId="admin-memo-category-select" />
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={ADMIN_MEMO_MAX_LENGTH}
              rows={3}
              placeholder={t('orders.adminMemos.placeholder', '메모 내용 (줄바꿈 가능)')}
              className={textareaClass}
              style={textareaStyle}
              aria-label={t('orders.adminMemos.content', '내용')}
              data-testid="admin-memo-content"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
                {content.length.toLocaleString()} / {ADMIN_MEMO_MAX_LENGTH.toLocaleString()}
              </span>
              <Button size="sm" disabled={!formValid || create.isPending} onClick={() => void submit()} data-testid="admin-memo-submit">
                {t('orders.adminMemos.submit', '등록')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.adminMemos.deleteTitle', '메모 삭제')}</DialogTitle>
            <DialogDescription>{t('orders.adminMemos.deleteConfirm', '이 메모를 삭제하시겠습니까? 목록에서 빠집니다.')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('orders.adminMemos.cancel', '취소')}</Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={remove.isPending} onClick={() => void confirmDelete()} data-testid="admin-memo-delete-confirm">
              {t('orders.adminMemos.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
